import React, { useState } from 'react';
import { Clock, ShieldCheck, Award, Users } from "lucide-react";

const features = [
  {
    icon: Clock,
    title: "Project Coordination",
    description: "We work to your construction program. If there's a scheduling issue, you'll hear about it from us first."
  },
  {
    icon: ShieldCheck,
    title: "Fully Licensed & Insured",
    description: "QBCC licensed and fully insured for commercial work. All documentation available on request."
  },
  {
    icon: Award,
    title: "Australian Standards",
    description: "Installations are carried out in accordance with applicable codes and project requirements."
  },
  {
    icon: Users,
    title: "Builder-Ready",
    description: "We understand construction sites. We fit in with your team, not the other way around."
  },
];

const whyPoints = [
  "We focus on clear communication and practical coordination on commercial sites.",
  "We don't disappear after rough-in — we see every job through to sign-off.",
  "We're straightforward to deal with. You get a quote, we do the work, done.",
  "If something comes up on site, we sort it — we don't wait to be told twice.",
];

const stats = [
  { value: "30+", label: "Years", sub: "Plumbing experience" },
  { value: "SEQ", label: "Wide", sub: "Service area" },
  { value: "Family", label: "Owned", sub: "& Operated" },
];

export default function AboutSection() {
  const [open, setOpen] = useState(false);

  return (
    <section id="about" className="bg-[#080808] py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-14 lg:px-20">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-start">

          {/* LEFT */}
          <div>
            <p className="text-[#60a5fa] font-semibold tracking-[0.2em] uppercase text-xs mb-5">About Us</p>
            <h2 className="font-bold text-white leading-[1.1] tracking-tight mb-6" style={{ fontSize: 'clamp(1.6rem, 5vw, 3rem)' }}>
              30+ Years of Plumbing<br />Experience Done Right.
            </h2>

            <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-5" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              Oliver &amp; Sons Plumbing brings over 30 years of plumbing experience. We provide commercial plumbing services across South East Queensland, working with builders, shopfitters and developers on hydraulic rough-ins, fit-outs and final fit-off.
            </p>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-5">
              Our primary focus is commercial plumbing for builders, shopfitters and commercial projects. Selected residential renovation projects may also be considered where the scope and requirements align with our services.
            </p>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-8" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              We're a family business. That means the person you call is the person who shows up, and the work gets done the way we'd want it done on our own projects. No hand-offs, no excuses.
            </p>

            {/* Expandable */}
            <div className="border-b border-[#2563eb]/25">
              <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-4 text-left group"
              >
                <span className="text-gray-300 text-sm font-medium group-hover:text-white transition-colors">
                  What builders can expect from us
                </span>
                <span
                  className="text-[#2563eb] text-lg font-bold transition-transform duration-300 flex-shrink-0"
                  style={{ transform: open ? 'rotate(45deg)' : 'rotate(0deg)', display: 'inline-block' }}
                >
                  +
                </span>
              </button>

              <div
                className="overflow-hidden transition-all duration-400 ease-in-out"
                style={{ maxHeight: open ? '320px' : '0px', opacity: open ? 1 : 0, transition: 'max-height 0.35s ease, opacity 0.3s ease' }}
              >
                <div className="pb-6 flex flex-col gap-3">
                  {whyPoints.map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-[#2563eb] text-xs font-bold mt-0.5 flex-shrink-0 w-4">{String(i + 1).padStart(2, '0')}</span>
                      <p className="text-gray-400 text-sm leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex flex-col gap-5">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-sm bg-[#1e3a5f]/30 border border-[#1e3a5f]/50 flex items-center justify-center flex-shrink-0 group-hover:border-[#2563eb]/50 transition-colors">
                  <f.icon className="w-5 h-5 text-[#5a9cf8]" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-white font-semibold text-sm md:text-base mb-1">{f.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed" style={{ overflowWrap: 'break-word' }}>{f.description}</p>
                </div>
              </div>
            ))}

            {/* Stats strip */}
            <div className="mt-4 border border-white/5 bg-[#0d0d0d] flex divide-x divide-white/5">
              {stats.map((s) => (
                <div key={s.value} className="flex-1 px-4 py-5 text-center">
                  <p className="text-white font-bold text-lg leading-none">
                    {s.value} <span className="text-[#5a9cf8]">{s.label}</span>
                  </p>
                  <p className="text-gray-400 text-xs mt-1.5">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
